import { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";

import { SocialProvider as LoginProvider } from "@reown/appkit";
import {
  PrepareConnectionResult,
  handleRedirectAndClose,
} from "@reown/web-wallet-core";

import { useWallet } from "../hooks/useWallet";
import { AppLogo } from "./ui/AppLogo";
import { AppProviderLogos } from "./ui/AppProviderLogos";
import { Container } from "./ui/Container";
import { DomainVerify } from "./ui/DomainVerify";
import { ErrorContent } from "./ui/ErrorContent";
import { Header } from "./ui/Header";
import { Permissions } from "./ui/Permissions";
import Spacer from "./ui/Spacer";
import { SuccessContent } from "./ui/SuccessContent";

type ConnectProps = {
  uri: string;
  provider?: string;
  appProjectId?: string;
};

type ConnectionStatus =
  | "loading"
  | "preparing"
  | "ready"
  | "connecting"
  | "approved"
  | "error";

export function Connect({ uri, provider, appProjectId }: ConnectProps) {
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<ConnectionStatus>("loading");
  const [connectionDetails, setConnectionDetails] =
    useState<PrepareConnectionResult | null>(null);

  const {
    wallet,
    error: walletError,
    isInitializing,
    isReady,
  } = useWallet({
    disconnectOnInit: true,
  });

  useEffect(() => {
    if (walletError) {
      setError(walletError);
    }
  }, [walletError]);

  const prepareConnection = async () => {
    if (!uri) {
      setError("No WalletConnect URI provided");
      return;
    }

    if (!wallet) {
      setError("Wallet not initialized");
      return;
    }

    try {
      setStatus("preparing");

      const connectionInfo = await Sentry.startSpan(
        {
          op: "wallet.prepareConnection",
          name: "Prepare WalletConnect Connection",
        },
        async (span) => {
          span.setAttribute("hasProvider", Boolean(provider));
          span.setAttribute("provider", provider ?? "unknown");
          span.setAttribute("hasAppProjectId", Boolean(appProjectId));
          const result = await wallet.prepareConnection({
            uri,
            loginProvider: provider as LoginProvider,
            appProjectId,
          });
          return result;
        }
      );

      setConnectionDetails(connectionInfo);

      setStatus("ready");
    } catch (err) {
      Sentry.captureException(err);
      setError(
        err instanceof Error ? err.message : "Failed to prepare connection"
      );
      setStatus("error");
    }
  };

  const handleProceed = async () => {
    if (!connectionDetails) {
      setError("Connection details not available");
      return;
    }

    try {
      setStatus("connecting");
      const session = await Sentry.startSpan(
        { op: "wallet.connect", name: "Approve WalletConnect Session" },
        async (span) => {
          span.setAttribute("hasConnectionDetails", Boolean(connectionDetails));
          const approved = await connectionDetails.approve();
          return approved;
        }
      );
      setStatus("approved");

      handleRedirectAndClose(session.peer.metadata);
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : "Failed to connect");
      setStatus("error");
    }
  };

  const handleReject = async () => {
    await Sentry.startSpan(
      { op: "wallet.reject", name: "Reject WalletConnect Session" },
      async () => {
        await connectionDetails?.reject();
      }
    );
    setError("Connection rejected");
    setStatus("error");
  };

  useEffect(() => {
    if (!isInitializing && wallet) {
      prepareConnection();
    }
  }, [wallet, provider, appProjectId, isInitializing]);

  const LoadingContent = () => (
    <>
      <AppLogo />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        {isInitializing
          ? "Initializing..."
          : !isReady
          ? "Loading..."
          : "Preparing connection..."}
      </wui-text>
      <Spacer size="2" />
    </>
  );

  const ReadyContent = () => {
    if (!connectionDetails) {
      return <ErrorContent title="Connection details not found" />;
    }

    return (
      <>
        <Header text="Connect to Provider" />
        <Spacer size="2" />
        {provider ? (
          <AppProviderLogos
            appIcon={connectionDetails.proposerMetadata.icons[0]}
            provider={provider}
          />
        ) : (
          <AppLogo logoUrl={connectionDetails?.proposerMetadata?.icons?.[0]} />
        )}
        <Spacer size="4" />
        <DomainVerify
          verifyContext={connectionDetails.verifyContext}
          peerMetadata={connectionDetails.proposerMetadata}
        />
        <Spacer size="2" />
        <Permissions provider={provider} />
        <Spacer size="2" />
        <wui-button
          size="lg"
          fullWidth
          variant="accent-primary"
          onClick={handleProceed}
        >
          <wui-flex columnGap="1">
            Proceed
            <wui-icon size="sm" color="accent-primary" name="chevronRight" />
          </wui-flex>
        </wui-button>
      </>
    );
  };

  const ConnectingContent = () => (
    <>
      <AppLogo logoUrl={connectionDetails?.proposerMetadata?.icons?.[0]} />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        Connecting to Reown...
      </wui-text>
      <Spacer size="2" />
      <wui-button size="lg" fullWidth variant="accent-primary" loading />
    </>
  );

  const ErrorFooter = () => (
    <wui-text variant="md-regular" color="secondary" align="center">
      Oops! The process didn't go through.
      <br />
      You may have rejected the connection or there could've been a provider
      issue.
    </wui-text>
  );

  const ReadyFooter = () => (
    <wui-flex alignItems="center">
      <wui-text variant="md-regular" color="secondary" align="center">
        Don't want to continue?
      </wui-text>
      <wui-link onClick={handleReject}>Reject Request</wui-link>
    </wui-flex>
  );

  const ConnectingFooter = () => (
    <wui-text variant="md-regular" color="secondary" align="center">
      After connecting, we'll take you back to the app.
    </wui-text>
  );

  const ApprovedFooter = () => (
    <wui-text variant="md-regular" color="secondary" align="center">
      You should be redirected to the app automatically.
      <br />
      If not, press the button above.
    </wui-text>
  );

  const renderContent = () => {
    if (error) {
      return (
        <ErrorContent
          title={error}
          peerMetadata={connectionDetails?.proposerMetadata}
        />
      );
    }

    if (status === "loading" || status === "preparing") {
      return <LoadingContent />;
    }

    if (status === "ready") {
      return <ReadyContent />;
    }

    if (status === "connecting") {
      return <ConnectingContent />;
    }

    if (status === "approved") {
      return (
        <SuccessContent
          title="Connection complete!"
          peerMetadata={connectionDetails?.proposerMetadata}
        />
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (error) {
      return <ErrorFooter />;
    }

    if (status === "ready" && connectionDetails) {
      return <ReadyFooter />;
    }

    if (status === "connecting") {
      return <ConnectingFooter />;
    }

    if (status === "approved") {
      return <ApprovedFooter />;
    }

    return null;
  };

  return (
    <>
      <Container>{renderContent()}</Container>
      <Spacer size="2" />
      {renderFooter()}
    </>
  );
}
